/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Why choose FalcoCut AI Product Avatar section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '⚡️',
    title: 'Fast Video Generation',
    description:
      'Turn a product photo into a polished avatar video in minutes. Skip cameras, studios, and long production days while keeping your content pipeline moving.',
  },
  {
    icon: '🌐',
    title: 'Multi-Language Voice Options',
    description:
      'Localize videos in multiple languages with natural-sounding AI voices. Expand into new regions without hiring creators or re-recording content.',
  },
  {
    icon: '🧩',
    title: 'Consistent Creators for Every Campaign',
    description:
      'Your AI avatars always look and sound the same, giving your brand a steady on-screen presence and a unified visual identity across channels.',
  },
  {
    icon: '🔒',
    title: 'Safe and Secure for Your Brand',
    description:
      'Your product images, scripts, and avatar assets stay protected under strong privacy and security standards, giving you confidence as you create.',
  },
  {
    icon: '✨',
    title: 'Test and Improve Faster',
    description:
      'Generate new variations in seconds to test hooks, scripts, and styles. Quickly find the creatives that drive stronger engagement and conversions.',
  },
  {
    icon: '💰',
    title: 'Lower Costs at Scale',
    description:
      'Create high-quality videos without models, equipment, or editing teams. Produce more ads and demos at a fraction of traditional production costs.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut for Your Product Avatars
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut helps you create high-performing product videos without filming, hiring
          creators, or managing complex production. Everything is built to save you time,
          cut costs, and help you launch more winning ads with less effort.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
